/**
 * Mirrors the Prisma enums (docs/PLAN.md §5) as const objects, so the frontend gets typed,
 * string-identical values without depending on the generated Prisma client. Kept in sync by
 * hand — a full schema with these exact enums lands in stage 1.
 */

export const StoreRole = {
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  MANAGER: 'MANAGER',
  COURIER: 'COURIER',
  CUSTOMER: 'CUSTOMER',
} as const;
export type StoreRole = (typeof StoreRole)[keyof typeof StoreRole];

export const MemberStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  BLOCKED: 'BLOCKED',
} as const;
export type MemberStatus = (typeof MemberStatus)[keyof typeof MemberStatus];

export const StoreStatus = {
  ACTIVE: 'ACTIVE',
  BLOCKED: 'BLOCKED',
} as const;
export type StoreStatus = (typeof StoreStatus)[keyof typeof StoreStatus];

export const StockMethod = {
  FEFO: 'FEFO',
  FIFO: 'FIFO',
} as const;
export type StockMethod = (typeof StockMethod)[keyof typeof StockMethod];

export const StockVisibility = {
  EXACT: 'EXACT',
  BINARY: 'BINARY',
  LEVELS: 'LEVELS',
  HIDDEN: 'HIDDEN',
} as const;
export type StockVisibility = (typeof StockVisibility)[keyof typeof StockVisibility];

export const VerificationPurpose = {
  EMAIL_VERIFY: 'EMAIL_VERIFY',
  PHONE_VERIFY: 'PHONE_VERIFY',
  PASSWORD_RESET: 'PASSWORD_RESET',
} as const;
export type VerificationPurpose = (typeof VerificationPurpose)[keyof typeof VerificationPurpose];

export const ImageKind = {
  PRODUCT: 'PRODUCT',
  CATEGORY: 'CATEGORY',
  STORE_LOGO: 'STORE_LOGO',
  CLAIM: 'CLAIM',
} as const;
export type ImageKind = (typeof ImageKind)[keyof typeof ImageKind];

export const OrderStatus = {
  NEW: 'NEW',
  CONFIRMED: 'CONFIRMED',
  ASSEMBLING: 'ASSEMBLING',
  SHIPPED: 'SHIPPED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];

export const OrderLineStatus = {
  ACTIVE: 'ACTIVE',
  REMOVED: 'REMOVED',
  BACKORDERED: 'BACKORDERED',
} as const;
export type OrderLineStatus = (typeof OrderLineStatus)[keyof typeof OrderLineStatus];

export const PaymentStatus = {
  UNPAID: 'UNPAID',
  PARTIAL: 'PARTIAL',
  PAID: 'PAID',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const PaymentMethod = {
  CASH: 'CASH',
  TRANSFER: 'TRANSFER',
  ONLINE: 'ONLINE',
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const DeliveryMethod = {
  DELIVERY: 'DELIVERY',
  PICKUP: 'PICKUP',
} as const;
export type DeliveryMethod = (typeof DeliveryMethod)[keyof typeof DeliveryMethod];

export const ReservationStatus = {
  ACTIVE: 'ACTIVE',
  CONSUMED: 'CONSUMED',
  RELEASED: 'RELEASED',
} as const;
export type ReservationStatus = (typeof ReservationStatus)[keyof typeof ReservationStatus];

export const ClaimStatus = {
  OPEN: 'OPEN',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  RESOLVED: 'RESOLVED',
} as const;
export type ClaimStatus = (typeof ClaimStatus)[keyof typeof ClaimStatus];

export const ClaimReason = {
  SHORTAGE: 'SHORTAGE',
  DEFECT: 'DEFECT',
  WRONG_ITEM: 'WRONG_ITEM',
  EXPIRED: 'EXPIRED',
  OTHER: 'OTHER',
} as const;
export type ClaimReason = (typeof ClaimReason)[keyof typeof ClaimReason];

export const NotificationType = {
  JOIN_REQUEST_CREATED: 'JOIN_REQUEST_CREATED',
  MEMBER_APPROVED: 'MEMBER_APPROVED',
  MEMBER_REJECTED: 'MEMBER_REJECTED',
  MEMBER_BLOCKED: 'MEMBER_BLOCKED',
  MEMBER_UNBLOCKED: 'MEMBER_UNBLOCKED',
  ORDER_CREATED: 'ORDER_CREATED',
  ORDER_STATUS_CHANGED: 'ORDER_STATUS_CHANGED',
  ORDER_LINES_ADJUSTED: 'ORDER_LINES_ADJUSTED',
  ORDER_MESSAGE: 'ORDER_MESSAGE',
  CLAIM_CREATED: 'CLAIM_CREATED',
  CLAIM_RESOLVED: 'CLAIM_RESOLVED',
  STOCK_ARRIVED: 'STOCK_ARRIVED',
  LOW_STOCK: 'LOW_STOCK',
  EXPIRING_BATCH: 'EXPIRING_BATCH',
  DEBT_OVERDUE: 'DEBT_OVERDUE',
  STOCK_VERIFY_MISMATCH: 'STOCK_VERIFY_MISMATCH',
} as const;
export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];

export const NotificationChannel = {
  IN_APP: 'IN_APP',
  WEB_PUSH: 'WEB_PUSH',
  TELEGRAM: 'TELEGRAM',
  SMS: 'SMS',
  EMAIL: 'EMAIL',
} as const;
export type NotificationChannel = (typeof NotificationChannel)[keyof typeof NotificationChannel];

export const ImportStatus = {
  UPLOADED: 'UPLOADED',
  VALIDATED: 'VALIDATED',
  APPLIED: 'APPLIED',
  FAILED: 'FAILED',
} as const;
export type ImportStatus = (typeof ImportStatus)[keyof typeof ImportStatus];

export const CounterpartyType = {
  SUPPLIER: 'SUPPLIER',
  CUSTOMER: 'CUSTOMER',
  BOTH: 'BOTH',
} as const;
export type CounterpartyType = (typeof CounterpartyType)[keyof typeof CounterpartyType];

export const DocumentType = {
  RECEIPT: 'RECEIPT',
  SALE: 'SALE',
  SALE_RETURN: 'SALE_RETURN',
  PURCHASE_RETURN: 'PURCHASE_RETURN',
  WRITE_OFF: 'WRITE_OFF',
  TRANSFER: 'TRANSFER',
  INVENTORY: 'INVENTORY',
  PAYMENT_IN: 'PAYMENT_IN',
  PAYMENT_OUT: 'PAYMENT_OUT',
} as const;
export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];

export const DocumentStatus = {
  DRAFT: 'DRAFT',
  POSTED: 'POSTED',
  CANCELLED: 'CANCELLED',
} as const;
export type DocumentStatus = (typeof DocumentStatus)[keyof typeof DocumentStatus];

export const WriteOffReason = {
  EXPIRED: 'EXPIRED',
  DEFECT: 'DEFECT',
  SHORTAGE: 'SHORTAGE',
  OTHER: 'OTHER',
} as const;
export type WriteOffReason = (typeof WriteOffReason)[keyof typeof WriteOffReason];
